import React, { useEffect, useState } from "react";
import { Users, CreditCard, FileStack, Database, Shield, Activity, TrendingUp, Clock } from "lucide-react";
import { adminApi } from "../lib/api";
import { useAuth } from "../lib/auth";

function StatCard({ icon: Icon, label, value, trend, color = "primary" }) {
  const colorClasses = {
    primary: "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white",
    accent: "bg-accent/10 text-accent group-hover:bg-accent group-hover:text-white",
    green: "bg-green-100 text-green-700 group-hover:bg-green-600 group-hover:text-white",
    blue: "bg-blue-100 text-blue-700 group-hover:bg-blue-600 group-hover:text-white",
  };

  return (
    <div className="bg-white border border-primary/15 rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all group">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center transition-colors ${colorClasses[color]}`}>
          <Icon size={16} strokeWidth={1.8} />
        </div>
        {trend && (
          <span className={`text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="font-display text-3xl font-semibold text-primary leading-none">
        {value}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/60 mt-2">
        {label}
      </div>
    </div>
  );
}

export default function AdminSuperDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.role !== "superadmin") {
      setError("Accès réservé aux super-administrateurs");
      setLoading(false);
      return;
    }

    adminApi.getSystemStats()
      .then(({ data }) => setStats(data))
      .catch((e) => setError(e?.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-sm text-foreground/50">Chargement des statistiques...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-sm">
        {error}
      </div>
    );
  }

  return (
    <div data-testid="super-admin-dashboard">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / super administration
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
          Tableau de bord Super Admin
        </h1>
        <p className="text-sm text-foreground/60 mt-2 max-w-3xl">
          Vue d'ensemble complète du système. Gérez les utilisateurs, les abonnements, la sécurité et la maintenance.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Users}
          label="Total Abonnés"
          value={stats?.total_subscribers ?? "—"}
          color="primary"
        />
        <StatCard
          icon={CreditCard}
          label="Abonnements Actifs"
          value={stats?.active_subscriptions ?? "—"}
          color="green"
        />
        <StatCard
          icon={Shield}
          label="Administrateurs"
          value={stats?.total_admins ?? "—"}
          color="accent"
        />
        <StatCard
          icon={FileStack}
          label="Documents"
          value={stats?.total_documents ?? "—"}
          color="blue"
        />
        <StatCard
          icon={Database}
          label="Chunks Vectoriels"
          value={stats?.total_chunks ?? "—"}
          color="primary"
        />
        <StatCard
          icon={Activity}
          label="Requêtes Aujourd'hui"
          value={stats?.total_queries_today ?? "—"}
          color="green"
        />
        <StatCard
          icon={TrendingUp}
          label="Feedback Total"
          value={stats?.total_feedback ?? "—"}
          color="accent"
        />
        <StatCard
          icon={Clock}
          label="En Attente"
          value={stats?.pending_feedback ?? "—"}
          color="blue"
        />
      </div>

      {/* Database Info */}
      {stats?.database_size_mb && (
        <div className="bg-white border border-primary/15 rounded-sm p-5 mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
            / base de données
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-primary">Taille de la base de données</h3>
              <p className="text-xs text-foreground/60 mt-1">
                Espace de stockage utilisé pour les documents, chunks et métadonnées
              </p>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-semibold text-primary">
                {stats.database_size_mb} <span className="text-sm font-normal text-foreground/60">MB</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-primary/15 rounded-sm p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-3">
            / gestion rapide
          </div>
          <h3 className="font-display text-lg font-semibold text-primary mb-4">Actions Rapides</h3>
          <div className="space-y-2">
            <a href="/admin/super/subscribers" className="block px-4 py-3 bg-primary/5 hover:bg-primary/10 rounded-sm text-sm text-primary transition-colors">
              → Gérer les abonnés
            </a>
            <a href="/admin/super/security" className="block px-4 py-3 bg-primary/5 hover:bg-primary/10 rounded-sm text-sm text-primary transition-colors">
              → Audit de sécurité
            </a>
            <a href="/admin/super/maintenance" className="block px-4 py-3 bg-primary/5 hover:bg-primary/10 rounded-sm text-sm text-primary transition-colors">
              → Maintenance
            </a>
            <a href="/admin/audit" className="block px-4 py-3 bg-primary/5 hover:bg-primary/10 rounded-sm text-sm text-primary transition-colors">
              → Journal d'audit complet
            </a>
          </div>
        </div>

        <div className="bg-white border border-primary/15 rounded-sm p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-3">
            / informations système
          </div>
          <h3 className="font-display text-lg font-semibold text-primary mb-4">État du Système</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center justify-between py-2 border-b border-primary/5">
              <span className="text-foreground/70">Rôle actuel</span>
              <span className="font-mono text-xs bg-accent/20 text-[#b4551c] px-2 py-1 rounded-sm">
                {user?.role}
              </span>
            </li>
            <li className="flex items-center justify-between py-2 border-b border-primary/5">
              <span className="text-foreground/70">Permissions</span>
              <span className="text-xs text-foreground/60">Tous les droits</span>
            </li>
            <li className="flex items-center justify-between py-2 border-b border-primary/5">
              <span className="text-foreground/70">Version API</span>
              <span className="font-mono text-xs">v1.0.0</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
